/**
 * Assumption Ledger panel: lists assumptions grouped by element_type with color coding.
 */
import React from 'react';
import { Collapse, Typography } from 'antd';
import type { AssumptionEntry, ConfidenceLevel } from '../../types/marketfitGuided';

const { Text } = Typography;

const CONFIDENCE_COLORS: Record<ConfidenceLevel, string> = {
  low: '#ff4d4f',
  medium: '#faad14',
  high: '#52c41a',
  verified: '#52c41a',
};

function formatLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface AssumptionLedgerPanelProps {
  assumptions: AssumptionEntry[] | null | undefined;
  defaultExpanded?: boolean;
}

export const AssumptionLedgerPanel: React.FC<AssumptionLedgerPanelProps> = ({
  assumptions,
  defaultExpanded = false,
}) => {
  if (!assumptions || assumptions.length === 0) return null;

  const byType = assumptions.reduce<Record<string, AssumptionEntry[]>>((acc, a) => {
    const key = a.element_type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  return (
    <Collapse
      defaultActiveKey={defaultExpanded ? ['assumptions'] : []}
      style={{ flex: '0 0 200px', minWidth: 180 }}
      items={[
        {
          key: 'assumptions',
          label: `Assumption Ledger (${assumptions.length})`,
          children: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Object.entries(byType).map(([elementType, entries]) => (
                <div key={elementType}>
                  <Text strong style={{ fontSize: 11, color: '#666' }}>
                    {formatLabel(elementType)}
                  </Text>
                  <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {entries.map((a) => (
                      <div
                        key={a.id}
                        style={{
                          padding: 6,
                          borderRadius: 4,
                          background: '#fafafa',
                          borderLeft: `3px solid ${CONFIDENCE_COLORS[a.confidence_level] ?? '#d9d9d9'}`,
                        }}
                      >
                        <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: a.description }}>
                          {a.description}
                        </Text>
                        <div style={{ marginTop: 4, fontSize: 10, color: '#999' }}>
                          {formatLabel(a.source)} · {formatLabel(a.validation_type)} ·{' '}
                          <span style={{ color: CONFIDENCE_COLORS[a.confidence_level] }}>
                            {formatLabel(a.confidence_level)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ),
        },
      ]}
    />
  );
};
